import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@parrot/utils';
import { ChevronDown, Check, X } from 'lucide-react';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
  /** Optional image URL (e.g. flag/logo) shown to the left of the label in trigger + list */
  image?: string;
  /** Optional React node (e.g. <PlatformIcon/>) shown to the left — takes precedence over image. */
  icon?: ReactNode;
}

export interface SelectProps {
  options: SelectOption[];
  /** Single mode only. */
  value?: string;
  /** Single mode only. */
  onChange?: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
  /** Icon shown before the selected label in the trigger — overrides option.image */
  icon?: ReactNode;
  /** Make the trigger shrink/grow to fit its content instead of filling the container */
  autoWidth?: boolean;
  /**
   * When provided, the trigger's chevron is replaced by a clickable × whenever
   * the Select has a value (clears it) or is `dismissible` (removes the filter).
   * Matches the licensing-intelligence FilterChip single-action-slot pattern.
   */
  onClear?: () => void;
  /** With `onClear`: show × even when empty, to remove the filter entirely. */
  dismissible?: boolean;
  /**
   * Multi-select mode. When true, the component works off `values`/`onValuesChange`
   * instead of `value`/`onChange`: rows toggle (stay open), the trigger summarises
   * the chosen set ("Animation +2"), and clearing empties the whole set. The
   * single-select props stay for backward compatibility and are ignored here.
   */
  multiple?: boolean;
  /** Selected values in multi mode. */
  values?: string[];
  /**
   * Short name of what's being filtered ("Genre"). Shown as a muted prefix once
   * a value is chosen, so "Genre: Animation +1" still says what it filters —
   * the placeholder ("All Genres") carries that while empty.
   */
  label?: string;
  /** Filter bars: style the trigger as "set" (gold) whenever it has a value. */
  highlightWhenSet?: boolean;
  /** Toggle callback in multi mode — receives the full next array. */
  onValuesChange?: (values: string[]) => void;
}

/** Lifted from location-intelligence: portaled dropdown with search, scroll shadows,
    HUD-style gold hover/selected rows. */
export function Select({
  options,
  value = '',
  onChange,
  placeholder = 'Select…',
  searchable = false,
  disabled = false,
  className,
  icon,
  autoWidth = false,
  onClear,
  dismissible = false,
  multiple = false,
  values: rawValues = [],
  onValuesChange,
  label,
  highlightWhenSet = false,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [scrollTop, setScrollTop] = useState(false);
  const [scrollBottom, setScrollBottom] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* Multi mode: an option with value '' is the "All …" row. It means "no
     filter", so it is never stored — picking it clears the set, and it shows
     as checked exactly when nothing else is. */
  const values = rawValues.filter((v) => v !== '');
  const valueSet = multiple ? new Set(values) : null;
  const selected = options.find((o) => o.value === value);
  const isChosen = (v: string) =>
    multiple ? (v === '' ? values.length === 0 : valueSet!.has(v)) : v === value;

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setScrollTop(false);
    setScrollBottom(false);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 'var(--z-dropdown)',
    });
  }, []);

  const openMenu = useCallback(() => {
    updateMenuPosition();
    setOpen(true);
  }, [updateMenuPosition]);

  const updateScrollShadows = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const { scrollTop: st, scrollHeight, clientHeight } = list;
    setScrollTop(st > 4);
    setScrollBottom(st + clientHeight < scrollHeight - 4);
  }, []);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(updateScrollShadows, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, filtered.length, updateScrollShadows]);

  useEffect(() => {
    if (!open) return undefined;
    const handleScroll = (e: Event) => {
      const menu = document.querySelector('[data-select-menu]');
      if (menu && menu.contains(e.target as Node)) return;
      close();
    };
    const handleResize = () => updateMenuPosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, close, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && wrapRef.current.contains(e.target as Node)) return;
      const menu = document.querySelector('[data-select-menu]');
      if (menu && menu.contains(e.target as Node)) return;
      close();
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleSelect = (optValue: string) => {
    if (multiple) {
      // "All" row → clear the set (and close: there's nothing more to pick).
      if (optValue === '') {
        onValuesChange?.([]);
        close();
        return;
      }
      // Toggle membership; keep the menu open for picking several.
      const next = valueSet!.has(optValue)
        ? values.filter((v) => v !== optValue)
        : [...values, optValue];
      onValuesChange?.(next);
      return;
    }
    onChange?.(optValue);
    close();
  };

  /** Trigger label in multi mode: first chosen option's label, "+N" for the rest. */
  const multiLabel = (() => {
    if (!multiple || values.length === 0) return null;
    const first = options.find((o) => o.value === values[0])?.label ?? values[0];
    return values.length === 1 ? first : `${first} +${values.length - 1}`;
  })();

  const triggerLeft: ReactNode =
    icon ??
    selected?.icon ??
    (selected?.image ? (
      <img src={selected.image} className={styles.optionImg} alt={''} />
    ) : null);

  const menu = open
    ? createPortal(
        <div data-select-menu className={styles.menu} style={menuStyle}>
          {searchable && (
            <div className={styles.search}>
              <input
                ref={searchRef}
                role={'searchbox'}
                type={'text'}
                className={styles.searchInput}
                placeholder={'Search…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
          <div
            className={styles.listWrap}
            data-scroll-top={scrollTop || undefined}
            data-scroll-bottom={scrollBottom || undefined}
          >
            <div ref={listRef} role={'listbox'} className={styles.list} onScroll={updateScrollShadows}>
              {filtered.length === 0 ? (
                <p className={styles.empty}>{'No results'}</p>
              ) : (
                filtered.map((opt) => (
                  <button
                    key={opt.value}
                    type={'button'}
                    role={'option'}
                    aria-selected={isChosen(opt.value)}
                    onClick={() => handleSelect(opt.value)}
                    className={cx(styles.option, isChosen(opt.value) && styles.optionSelected)}
                  >
                    {opt.icon ? (
                      <span className={styles.optionIcon} aria-hidden={'true'}>
                        {opt.icon}
                      </span>
                    ) : (
                      opt.image && (
                        <img
                          src={opt.image}
                          className={styles.optionImg}
                          alt={''}
                          loading={'lazy'}
                          onError={(e) => {
                            e.currentTarget.style.opacity = '0';
                          }}
                        />
                      )
                    )}
                    <span className={styles.optionLabel}>{opt.label}</span>
                    {isChosen(opt.value) && <Check size={13} className={styles.checkIcon} />}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const hasValue = multiple ? values.length > 0 : value !== '';
  const showClear = onClear !== undefined && (hasValue || dismissible);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasValue) {
      if (multiple) onValuesChange?.([]);
      else onChange?.('');
    } else {
      onClear?.();
    }
    close();
  };

  return (
    <div ref={wrapRef} className={cx(styles.wrap, autoWidth && styles.autoWidth, className)}>
      <button
        ref={triggerRef}
        type={'button'}
        role={'combobox'}
        aria-expanded={open}
        aria-haspopup={'listbox'}
        disabled={disabled}
        onClick={() => !disabled && (open ? close() : openMenu())}
        className={cx(
          styles.trigger,
          highlightWhenSet && hasValue && styles.triggerSet,
          open && styles.triggerOpen,
        )}
      >
        {triggerLeft && (
          <span className={styles.iconLeft} aria-hidden={'true'}>
            {triggerLeft}
          </span>
        )}
        <span className={cx(styles.triggerValue, !hasValue && styles.placeholder)}>
          {label && hasValue && <span className={styles.triggerLabel}>{label}: </span>}
          {multiple ? (multiLabel ?? placeholder) : selected ? selected.label : placeholder}
        </span>
        {showClear ? (
          <span
            className={styles.clear}
            onClick={handleClear}
            role={'button'}
            aria-label={hasValue ? 'Clear selection' : 'Remove filter'}
            title={hasValue ? 'Clear selection' : 'Remove filter'}
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} className={cx(styles.chevron, open && styles.chevronOpen)} />
        )}
      </button>
      {menu}
    </div>
  );
}
